import { Link } from 'react-router-dom'
import { IconArrowRight, IconCalendarEvent, IconBed, IconUser } from '@tabler/icons-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useUpcomingReservations } from '@/hooks/useUpcomingReservations'

const estatColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  CHECKED_IN: 'bg-green-100 text-green-800',
  CHECKED_OUT: 'bg-gray-100 text-gray-800',
  CANCELLED: 'bg-red-100 text-red-800',
}

const estatLabels: Record<string, string> = {
  PENDING: 'Pendent',
  CONFIRMED: 'Confirmada',
  CHECKED_IN: 'Check-in',
  CHECKED_OUT: 'Check-out',
  CANCELLED: 'Cancel·lada',
}

export default function UpcomingReserves() {
  const { data: reserves = [], isLoading: loading } = useUpcomingReservations()

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return dateString

      return date.toLocaleDateString('ca-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    } catch {
      return dateString
    }
  }

  if (loading) {
    return (
      <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-2xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Reserves Properes (7 dies)</h2>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-200 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  if (reserves.length === 0) {
    return (
      <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-2xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Reserves Properes (7 dies)</h2>
        </div>
        <div className="text-center py-12">
          <IconCalendarEvent className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hi ha reserves pels propers 7 dies</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-2xl p-6 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">Reserves Properes (7 dies)</h2>
        <Link to="/reserves">
          <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700">
            Veure totes
            <IconArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>

      {/* Desktop View - Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">
                Codi
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">
                Hoste
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">
                Check-in
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">
                Check-out
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">
                Habitació
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">
                Estat
              </th>
            </tr>
          </thead>
          <tbody>
            {reserves.map((reserve) => (
              <tr
                key={reserve.id}
                className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <td className="py-3 px-4">
                  <span className="font-mono text-sm font-medium text-gray-900">
                    {reserve.beds24_id || reserve.id}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center space-x-2">
                    <IconUser className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-900">{reserve.guest_name}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-gray-700">
                  {formatDate(reserve.data_entrada)}
                </td>
                <td className="py-3 px-4 text-sm text-gray-700">
                  {formatDate(reserve.data_sortida)}
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center space-x-2">
                    <IconBed className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-900">
                      {reserve.unit?.displayName || reserve.unit?.name || reserve.room?.name || '-'}
                    </span>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <Badge className={estatColors[reserve.estat] || 'bg-gray-100 text-gray-800'}>
                    {estatLabels[reserve.estat] || reserve.estat}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile View - Cards */}
      <div className="md:hidden space-y-3">
        {reserves.map((reserve) => (
          <div
            key={reserve.id}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="font-mono text-sm font-bold text-gray-900">
                  {reserve.beds24_id || reserve.id}
                </p>
                <p className="text-sm text-gray-700 mt-1">{reserve.guest_name}</p>
              </div>
              <Badge className={estatColors[reserve.estat] || 'bg-gray-100 text-gray-800'}>
                {estatLabels[reserve.estat] || reserve.estat}
              </Badge>
            </div>

            <div className="space-y-1 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <IconCalendarEvent className="w-4 h-4" />
                <span>{formatDate(reserve.data_entrada)} → {formatDate(reserve.data_sortida)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <IconBed className="w-4 h-4" />
                <span>{reserve.unit?.displayName || reserve.unit?.name || reserve.room?.name || '-'}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
